import { deepMerge } from 'components/transaction/components/panels/transaction/announcements/appointmentsTable/appointmentHelper';
import { axiosAiq } from 'config';
import { omit } from 'lodash';
import { call, put, select } from 'redux-saga/effects';
import { APPOINTMENTS_TABLE_NAME, AUTOMATION_SOURCE } from 'utils/constants';
import { notificationHandler } from 'utils/utils';
import { selectTransactionsSplitViewMode } from 'store/transactionsSlice/transactionsSelectors';
import { selectFormById } from '../../formControlSelectors';
import { formControlActions } from '../../formControlSlice';
import { copyLocationTransactionPartialHandler } from './copyLocationTransactionHandlers';
import { beginCopy, dropIfStale, endCopy } from '../../lib/copyRequestGuard';

export function* copyAppointmentDataHandler({ payload: { transactionId, values, copy, appointmentId, reopenTransaction } }) {
    // The source row lets a cell copy from this same appointment leave the loading copy alone (same data).
    const copySource = copy ? { tableName: APPOINTMENTS_TABLE_NAME, key: appointmentId } : null;
    const copyId = yield call(beginCopy, transactionId, copySource);
    // No open form to copy into.
    if (copyId == null) return;
    try {
        const existingForm = yield select(selectFormById(transactionId));
        if (copy) {
            try {
                const initialTransactionData = existingForm?.transactionData || {};
                const currTransactionData =
                    initialTransactionData?.appointment?.id !== undefined
                        ? (yield axiosAiq.get(`/transactions/trucks/${transactionId}`)).data
                        : initialTransactionData;
                if (yield call(dropIfStale, transactionId, copyId, copySource)) return;

                const transactionData = deepMerge(currTransactionData, {
                    ...omit(values, ['number', 'plannedTime', 'status', 'waitingReason']),
                    id: transactionId,
                    appointment: { id: appointmentId },
                    gate: { name: currTransactionData?.gate?.name },
                });

                yield put(formControlActions.setTransactionData({ transactionData }));
            } catch (err) {
                // A superseded copy fails silently: its result would have been dropped anyway.
                if (yield call(dropIfStale, transactionId, copyId, copySource)) return;
                yield notificationHandler({ err, title: `ERROR COPYING DATA TO FORM` });
                return;
            }
        } else {
            const reopenedAppointmentId = reopenTransaction?.appointment?.id ?? null;
            const transactionData = {
                ...existingForm?.transactionData,
                appointment: reopenedAppointmentId != null ? { id: reopenedAppointmentId } : null,
            };
            yield put(formControlActions.setTransactionData({ transactionData }));
        }

        const tableName = copy ? APPOINTMENTS_TABLE_NAME : '';
        const isSplitViewMode = yield select(selectTransactionsSplitViewMode);
        yield put(
            formControlActions.setAutomationValues(
                copy
                    ? { transactionId, automationObjTemplate: { source: AUTOMATION_SOURCE.APPOINTMENT }, isSplitViewMode }
                    : { transactionId, setAlisaAutomation: true, isSplitViewMode }
            )
        );

        yield put(formControlActions.setActiveSelectionTableName({ formId: transactionId, tableName }));
        yield put(formControlActions.validateTransactionWithInventoryData({ formId: transactionId }));
    } finally {
        yield call(endCopy, transactionId, copyId);
    }
}

export function* copyAppointmentDataPartialHandler({ payload }) {
    yield call(copyLocationTransactionPartialHandler, {
        payload: { ...payload, copySourceTable: APPOINTMENTS_TABLE_NAME, _sourceOverride: AUTOMATION_SOURCE.APPOINTMENT },
    });
}
